import axios, { AxiosResponse } from 'axios'
import { NotaView, PageResponseNotaList } from './_models'
import { ArquivoDto } from '../../../atendimento/core/_models'
import { AtualizaValorAdicionalDto } from '../../faturas-list/core/_models'

const API_URL = process.env.REACT_APP_API_URL
const FINANCEIRO_NOTAS_URL = `${API_URL}/financeiro/nota`
const FINANCEIRO_NOTAS_PROFISSIONAL_URL = `${API_URL}/financeiro/nota/profissional`

/**
 * Busca notas
 * @param query string
 * @returns PageResponseNotaList
 */
export const getNotas = (query: string): Promise<PageResponseNotaList> => {
  return axios
    .get(`${FINANCEIRO_NOTAS_URL}?${query}`)
    .then((resp: AxiosResponse<PageResponseNotaList>) => resp.data)
}

/**
 * Busca uma nota
 * @param idNota ID da nota
 * @returns NotaView
 */
export const getNotaById = (idNota: number): Promise<NotaView> => {
  return axios
    .get(`${FINANCEIRO_NOTAS_URL}/${idNota}`)
    .then((resp: AxiosResponse<NotaView>) => resp.data)
}

/**
 * Busca notas de profissional
 * @param query string
 * @returns PageResponseNotaList
 */
export const getNotasByProfissional = (query: string): Promise<PageResponseNotaList> => {
  return axios
    .get(`${FINANCEIRO_NOTAS_PROFISSIONAL_URL}?${query}`)
    .then((resp: AxiosResponse<PageResponseNotaList>) => resp.data)
}

/**
 * Busca uma nota de profissional
 * @param idNota ID da nota
 * @returns NotaView
 */
export const getNotaByProfissionalById = (idNota: number): Promise<NotaView> => {
  return axios
    .get(`${FINANCEIRO_NOTAS_PROFISSIONAL_URL}/${idNota}`)
    .then((resp: AxiosResponse<NotaView>) => resp.data)
}

/**
 * Aprova uma nota (envia para pagamento)
 * @param idNota ID da nota
 * @returns NotaView
 */
export const aprovarNota = (idNota: number): Promise<NotaView> => {
  return axios
    .patch(`${FINANCEIRO_NOTAS_URL}/${idNota}/aprova`)
    .then((resp: AxiosResponse<NotaView>) => resp.data)
}

/**
 * Upload de nota do profissional
 * @param idNota ID da nota
 * @param pdf File
 * @param xml File
 * @returns NotaView
 */
export const uploadNota = (idNota: number, pdf: File, xml: File): Promise<NotaView> => {
  const data = new FormData()

  data.append('pdf', pdf)
  data.append('xml', xml)

  return axios
    .put(`${FINANCEIRO_NOTAS_PROFISSIONAL_URL}/upload/${idNota}`, data)
    .then((resp: AxiosResponse<NotaView>) => resp.data)
}

/**
 * Marca uma nota como paga (envia comprovante)
 * @param idNota ID da nota
 * @param pdf File
 * @returns NotaView
 */
export const pagarNota = (idNota: number, pdf: File): Promise<NotaView> => {
  const data = new FormData()
  data.append('comprovante', pdf)

  return axios
    .patch(`${FINANCEIRO_NOTAS_URL}/${idNota}/paga`, data)
    .then((resp: AxiosResponse<NotaView>) => resp.data)
}

/**
 * Rejeita uma nota (devolve para o profissional)
 * @param idNota ID da nota
 * @param motivo string
 * @returns NotaView
 */
export const rejeitaNota = (idNota: number, motivo: string): Promise<NotaView> => {
  const data = new FormData()
  data.append('motivo', motivo)

  return axios
    .patch(`${FINANCEIRO_NOTAS_URL}/${idNota}/rejeita`, data)
    .then((resp: AxiosResponse<NotaView>) => resp.data)
}

/**
 * Cancela uma nota (em análise)
 * @param idNota ID da nota
 * @param motivo string
 * @returns NotaView
 */
export const cancelarNota = (idNota: number, motivo: string): Promise<NotaView> => {
  const data = new FormData()
  data.append('motivo', motivo)

  return axios
    .patch(`${FINANCEIRO_NOTAS_URL}/${idNota}/cancela`, data)
    .then((resp: AxiosResponse<NotaView>) => resp.data)
}

/**
 * Atualiza valor adicional da nota
 * @param idNota ID da nota
 * @param data AtualizaValorAdicionalDto
 * @returns NotaView
 */
export const updateValorNota = (
  idNota: number,
  data: AtualizaValorAdicionalDto
): Promise<NotaView> => {
  return axios
    .patch(`${FINANCEIRO_NOTAS_URL}/${idNota}/adicional`, data)
    .then((resp: AxiosResponse<NotaView>) => resp.data)
}

/**
 * Autoriza uma nota (com valor adicional)
 * @param idNota ID da nota
 * @param data AtualizaValorAdicionalDto
 * @returns NotaView
 */
export const autorizaNotaComValorAdicional = (
  idNota: number,
  data: AtualizaValorAdicionalDto
): Promise<NotaView> => {
  return axios
    .patch(`${FINANCEIRO_NOTAS_URL}/${idNota}/autoriza`, data)
    .then((resp: AxiosResponse<NotaView>) => resp.data)
}

/**
 * Download da nota
 * @param idNota ID da nota
 * @returns ArquivoDto
 */
export const downloadNota = (idNota: number): Promise<ArquivoDto> => {
  return axios
    .get(`${FINANCEIRO_NOTAS_URL}/download/${idNota}`)
    .then((resp: AxiosResponse<ArquivoDto>) => resp.data)
}

/**
 * Download do comprovante de pagamento
 * @param idNota ID da nota
 * @returns ArquivoDto
 */
export const downloadComprovantePagamento = (idNota: number): Promise<ArquivoDto> => {
  return axios
    .get(`${FINANCEIRO_NOTAS_URL}/download/pagamento/${idNota}`)
    .then((resp: AxiosResponse<ArquivoDto>) => resp.data)
}

/**
 * Download da nota de profissional
 * @param idNota ID da nota
 * @returns ArquivoDto
 */
export const downloadNotaByProfissional = (idNota: number): Promise<ArquivoDto> => {
  return axios
    .get(`${FINANCEIRO_NOTAS_PROFISSIONAL_URL}/download/${idNota}`)
    .then((resp: AxiosResponse<ArquivoDto>) => resp.data)
}

/**
 * Download do comprovante de pagamento do profissional
 * @param idNota ID da nota
 * @returns ArquivoDto
 */
export const downloadComprovantePagamentoByProfissional = (idNota: number): Promise<ArquivoDto> => {
  return axios
    .get(`${FINANCEIRO_NOTAS_PROFISSIONAL_URL}/download/pagamento/${idNota}`)
    .then((resp: AxiosResponse<ArquivoDto>) => resp.data)
}
